// iter-121.116 Phase 2 — Cockpit Scope Context
//
// Provides the platform-cockpit scope (period + org + tenant + owner) to
// every widget below the CockpitScopeBar. Loads the SA's saved default
// scope on mount, exposes setters, and persists "Save as default view".
//
// Widgets consume via `useCockpitScope()` and pass the values directly
// into their fetch URLs.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const CockpitScopeContext = createContext(null);

const DEFAULT_SCOPE = {
    period: "30d",
    custom_start: null,
    custom_end: null,
    org_id: null,
    tenant_id: null,
    owner_id: null,
};

export function CockpitScopeProvider({ children }) {
    const [scope, setScope] = useState(DEFAULT_SCOPE);
    const [loaded, setLoaded] = useState(false);
    const [saving, setSaving] = useState(false);
    const [options, setOptions] = useState({ orgs: [], tenants: [], owners: [] });
    const [optionsLoading, setOptionsLoading] = useState(false);

    // Load saved default scope on mount.
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await api.get("/home/cockpit/scope/preferences");
                if (!alive) return;
                const d = r.data || {};
                setScope({
                    period: d.period || "30d",
                    custom_start: d.custom_start || null,
                    custom_end: d.custom_end || null,
                    org_id: d.org_id || null,
                    tenant_id: d.tenant_id || null,
                    owner_id: d.owner_id || null,
                });
            } catch {
                // 403 (non-SA) or 5xx — fall back to defaults silently.
            } finally {
                if (alive) setLoaded(true);
            }
        })();
        return () => { alive = false; };
    }, []);

    // Refetch scope-options whenever period or org_id changes (tenants +
    // owners cascade from those). Includes custom start/end so the
    // "owners with activity in period" list reflects the custom range.
    useEffect(() => {
        if (!loaded) return;
        (async () => {
            setOptionsLoading(true);
            try {
                const params = new URLSearchParams({ period: scope.period });
                if (scope.custom_start) params.set("custom_start", scope.custom_start);
                if (scope.custom_end)   params.set("custom_end",   scope.custom_end);
                if (scope.org_id) params.set("org_id", scope.org_id);
                const r = await api.get(`/home/cockpit/scope/options?${params}`);
                setOptions({
                    orgs: r.data?.orgs || [],
                    tenants: r.data?.tenants || [],
                    owners: r.data?.owners || [],
                });
            } catch {
                setOptions({ orgs: [], tenants: [], owners: [] });
            } finally {
                setOptionsLoading(false);
            }
        })();
    }, [scope.period, scope.custom_start, scope.custom_end, scope.org_id, loaded]);

    // Patch helpers — null/empty string clears the field.
    const setPeriod = useCallback((period) => {
        setScope((s) => ({
            ...s,
            period: period || "30d",
            // Switching away from custom clears the dates.
            custom_start: period === "custom" ? s.custom_start : null,
            custom_end:   period === "custom" ? s.custom_end   : null,
        }));
    }, []);

    const setCustomRange = useCallback((start, end) => {
        setScope((s) => ({
            ...s,
            period: "custom",
            custom_start: start || null,
            custom_end: end || null,
        }));
    }, []);

    const setOrgId = useCallback((org_id) => {
        // Switching org clears tenant + owner (they belonged to the prior org).
        setScope((s) => ({
            ...s,
            org_id: org_id || null,
            tenant_id: null,
            owner_id: null,
        }));
    }, []);

    const setTenantId = useCallback((tenant_id) => {
        setScope((s) => ({ ...s, tenant_id: tenant_id || null }));
    }, []);

    const setOwnerId = useCallback((owner_id) => {
        setScope((s) => ({ ...s, owner_id: owner_id || null }));
    }, []);

    const reset = useCallback(() => setScope(DEFAULT_SCOPE), []);

    const saveAsDefault = useCallback(async () => {
        setSaving(true);
        try {
            await api.put("/home/cockpit/scope/preferences", scope);
            return true;
        } catch {
            return false;
        } finally {
            setSaving(false);
        }
    }, [scope]);

    // Build a queryString helper that widgets can append to their fetches.
    const queryString = useMemo(() => {
        const p = new URLSearchParams({ period: scope.period });
        if (scope.custom_start) p.set("custom_start", scope.custom_start);
        if (scope.custom_end)   p.set("custom_end",   scope.custom_end);
        if (scope.org_id)       p.set("org_id",       scope.org_id);
        if (scope.tenant_id)    p.set("tenant_id",    scope.tenant_id);
        if (scope.owner_id)     p.set("owner_id",     scope.owner_id);
        return p.toString();
    }, [scope]);

    const value = useMemo(() => ({
        scope, options, optionsLoading, loaded, saving,
        setPeriod, setCustomRange, setOrgId, setTenantId, setOwnerId,
        reset, saveAsDefault, queryString,
    }), [scope, options, optionsLoading, loaded, saving,
        setPeriod, setCustomRange, setOrgId, setTenantId, setOwnerId,
        reset, saveAsDefault, queryString]);

    return (
        <CockpitScopeContext.Provider value={value}>
            {children}
        </CockpitScopeContext.Provider>
    );
}

export function useCockpitScope() {
    const ctx = useContext(CockpitScopeContext);
    if (!ctx) {
        throw new Error("useCockpitScope must be used inside <CockpitScopeProvider>");
    }
    return ctx;
}

// Convenience: pull just the queryString (most widgets only need this).
export function useCockpitQueryString() {
    return useCockpitScope().queryString;
}
